/**
 * Program-scoped persona associations from the Prism RBAC inventory.
 *
 * A user holds personas per program, not in the abstract. One person can be a
 * student in Medicine and a program admin in Nursing, or student and faculty in
 * the same program. `programPersonas` is the source of truth; legacy `opensAs`
 * and `administeredProgramIds` are synthesized when it is absent.
 */

import {
  getLoginSession,
  isWorkspaceRole,
  type WorkspaceRole,
} from "@/lib/login-session"
import type { ScopeConfig, ScopeParent } from "@/lib/scope-switcher"
import {
  isModuleAdminRole,
  isTenantAdminTier,
  type ModuleAdminRole,
  type WorkspacePersona,
} from "@/lib/workspace-rbac"

/** Demo default when a flow names personas but not a program. */
export const DEFAULT_DEMO_PROGRAM_ID = "som"

/** Door order on product cards: student, then faculty, then program admin. */
const DOOR_ORDER: readonly WorkspaceRole[] = ["student", "member", "administrator"]

export interface ProgramPersonaGrant {
  programId: string
  personas: WorkspacePersona[]
  /** Module admin roles when the admin persona is held in this program. */
  moduleAdminRoles?: ModuleAdminRole[]
}

export function isProgramPersonaGrant(value: unknown): value is ProgramPersonaGrant {
  if (typeof value !== "object" || value === null) return false
  const grant = value as Record<string, unknown>
  if (typeof grant.programId !== "string" || grant.programId.length === 0) return false
  if (!Array.isArray(grant.personas)) return false
  const personas = grant.personas.filter(
    (p): p is WorkspacePersona => p === "student" || p === "faculty" || p === "admin",
  )
  if (personas.length === 0) return false
  return true
}

export function personaToWorkspaceRole(persona: WorkspacePersona): WorkspaceRole {
  if (persona === "student") return "student"
  if (persona === "faculty") return "member"
  return "administrator"
}

export function workspaceRoleToPersona(role: WorkspaceRole): WorkspacePersona | null {
  if (role === "student") return "student"
  if (role === "member") return "faculty"
  if (role === "administrator") return "admin"
  return null
}

function uniquePersonas(personas: WorkspacePersona[]): WorkspacePersona[] {
  const order: WorkspacePersona[] = ["student", "faculty", "admin"]
  return order.filter(persona => personas.includes(persona))
}

function normalizeGrant(raw: ProgramPersonaGrant): ProgramPersonaGrant {
  const moduleAdminRoles = raw.moduleAdminRoles?.filter(isModuleAdminRole)
  return {
    programId: raw.programId,
    personas: uniquePersonas(raw.personas),
    moduleAdminRoles:
      moduleAdminRoles && moduleAdminRoles.length > 0 ? moduleAdminRoles : undefined,
  }
}

export function normalizeProgramPersonas(value: unknown): ProgramPersonaGrant[] | null {
  if (!Array.isArray(value)) return null
  const grants = value.filter(isProgramPersonaGrant).map(normalizeGrant)
  return grants.length > 0 ? grants : null
}

/** Synthesize grants from older session fields for browsers mid-migration. */
export function synthesizeProgramPersonas(): ProgramPersonaGrant[] {
  const session = getLoginSession()
  const grants = new Map<string, WorkspacePersona[]>()

  const add = (programId: string, persona: WorkspacePersona) => {
    const existing = grants.get(programId) ?? []
    grants.set(programId, uniquePersonas([...existing, persona]))
  }

  const defaultProgram = DEFAULT_DEMO_PROGRAM_ID

  if (session.role === "student" || session.opensAs.includes("student")) {
    add(defaultProgram, "student")
  }
  if (session.role === "member" || session.opensAs.includes("member")) {
    add(defaultProgram, "faculty")
  }
  if (session.role === "administrator") {
    const programs = session.administeredProgramIds ?? [defaultProgram]
    for (const programId of programs) {
      add(programId, "admin")
    }
  }

  return [...grants.entries()].map(([programId, personas]) => ({
    programId,
    personas,
    moduleAdminRoles:
      session.moduleAdminRoles && personas.includes("admin")
        ? session.moduleAdminRoles
        : undefined,
  }))
}

/** Every program association this session holds. */
export function programPersonasForSession(): ProgramPersonaGrant[] {
  const stored = normalizeProgramPersonas(getLoginSession().programPersonas)
  if (stored && stored.length > 0) return stored
  return synthesizeProgramPersonas()
}

export function grantForProgram(programId: string): ProgramPersonaGrant | undefined {
  return programPersonasForSession().find(grant => grant.programId === programId)
}

export function personasInProgram(programId: string | null): WorkspacePersona[] {
  if (!programId) return []
  return grantForProgram(programId)?.personas ?? []
}

/** Identities available on a product card for the selected program. */
export function openableRolesInProgram(programId: string | null): WorkspaceRole[] {
  const personas = personasInProgram(programId)
  if (personas.length === 0) {
    const session = getLoginSession()
    if (!programId && session.opensAs.length > 1) {
      return DOOR_ORDER.filter(role => session.opensAs.includes(role))
    }
    return []
  }
  const roles = personas.map(personaToWorkspaceRole)
  const unique = DOOR_ORDER.filter(role => roles.includes(role))
  return unique.length > 1 ? unique : []
}

/** Programs where this session holds the student persona. */
export function studentProgramIds(): string[] {
  return programPersonasForSession()
    .filter(grant => grant.personas.includes("student"))
    .map(grant => grant.programId)
}

/** Programs where this session holds the admin persona. */
export function adminProgramIds(): ProgramPersonaGrant[] {
  return programPersonasForSession().filter(grant => grant.personas.includes("admin"))
}

export function moduleAdminRolesInProgram(programId: string | null): ModuleAdminRole[] {
  if (!programId) return []
  const grant = grantForProgram(programId)
  if (!grant?.personas.includes("admin")) return []
  return grant.moduleAdminRoles ?? []
}

export function hasPersonaInProgram(
  programId: string | null,
  persona: WorkspacePersona,
): boolean {
  return personasInProgram(programId).includes(persona)
}

/** Whether the session may pick a program, or is tied to one enrollment. */
export function hasMultipleStudentPrograms(): boolean {
  return studentProgramIds().length > 1
}

/** Programs where this session holds any persona grant. */
export function programIdsForSession(): string[] {
  return programPersonasForSession().map(grant => grant.programId)
}

/** More than one program grant (e.g. student in Medicine, admin in Nursing). */
export function hasMultipleProgramGrants(): boolean {
  return programIdsForSession().length > 1
}

/** Workspace roles held in one program, including when there is only one. */
export function identityRolesInProgram(programId: string | null): WorkspaceRole[] {
  const personas = personasInProgram(programId)
  if (personas.length === 0) return []
  const roles = personas.map(personaToWorkspaceRole)
  return DOOR_ORDER.filter(role => roles.includes(role))
}

/** Default active identity when the workspace lands on a program. */
export function defaultRoleForProgram(programId: string): WorkspaceRole | null {
  const roles = identityRolesInProgram(programId)
  return roles[0] ?? null
}

/**
 * Program to select when switching identity. Stays on the current program when
 * it already supports the role; otherwise picks the first grant that does.
 */
export function programIdForRoleSwitch(
  role: WorkspaceRole,
  currentProgramId: string | null,
): string | null {
  const persona = workspaceRoleToPersona(role)
  if (!persona) return null
  const matching = programPersonasForSession().filter(grant =>
    grant.personas.includes(persona),
  )
  if (matching.length === 0) return null
  if (currentProgramId && matching.some(grant => grant.programId === currentProgramId)) {
    return null
  }
  return matching[0]!.programId
}

/** Faculty and program admin in one session, without a student persona. */
export function isFacultyAdminCombo(): boolean {
  const personas = new Set(programPersonasForSession().flatMap(grant => grant.personas))
  return personas.has("faculty") && personas.has("admin") && !personas.has("student")
}

/** Tenet admin and Faculty + Admin may switch schools; everyone else is program-only. */
export function canChangeSchool(): boolean {
  return isTenantAdminTier() || isFacultyAdminCombo()
}

function sessionProgramIds(): Set<string> {
  const grantIds = new Set(programIdsForSession())
  const administered = getLoginSession().administeredProgramIds
  if (administered) {
    for (const id of administered) grantIds.add(id)
  }
  return grantIds
}

/** Lock the picker to one school while program switching stays available. */
export function lockSchoolScopeConfig(config: ScopeConfig): ScopeConfig {
  if (config.family !== "school") return config

  const grantIds = sessionProgramIds()
  const matchingParents = config.parents.filter(parent =>
    config.childrenOf(parent).some(child => grantIds.has(child.id)),
  )

  const locked =
    matchingParents.find(parent => parent.id === config.defaultParent.id) ??
    matchingParents[0] ??
    config.defaultParent

  const childrenOf = (parent: ScopeParent) => {
    const children = config.childrenOf(parent)
    // Administrators see every program at their school. A module admin's
    // grant list is which apps they open, not a one-program menu.
    if (getLoginSession().role === "administrator") return children
    if (grantIds.size === 0) return children
    return children.filter(child => grantIds.has(child.id))
  }

  return {
    ...config,
    parents: [locked],
    childrenOf,
    ariaSuffix: "Switch program",
  }
}

/**
 * School the program menu should list.
 *
 * The stored school wins when this session still has programs there. Otherwise
 * the first school that does, so a Johns Hopkins admin is not left on Mayo
 * with an empty list.
 */
export function sessionMenuParent(
  config: ScopeConfig,
  storedParentId: string | null | undefined,
): ScopeParent {
  const resolved = resolveScopeConfigForSession(config)
  const stored = storedParentId
    ? resolved.parents.find(parent => parent.id === storedParentId)
    : undefined
  if (stored && resolved.childrenOf(stored).length > 0) return stored
  return (
    resolved.parents.find(parent => resolved.childrenOf(parent).length > 0) ??
    resolved.defaultParent
  )
}

/** Persona grants, then school lock when this session cannot change school. */
export function resolveScopeConfigForSession(config: ScopeConfig): ScopeConfig {
  const filtered = filterScopeConfigForSession(config)
  return canChangeSchool() ? filtered : lockSchoolScopeConfig(filtered)
}

/** Narrow the scope menu to programs this session holds a persona grant for. */
export function filterScopeConfigForSession(config: ScopeConfig): ScopeConfig {
  const stored = normalizeProgramPersonas(getLoginSession().programPersonas)
  if (!stored || stored.length === 0) return config

  const grantIds = new Set(stored.map(grant => grant.programId))
  const childrenOf = (parent: ScopeParent) =>
    config.childrenOf(parent).filter(child => grantIds.has(child.id))
  const parents = config.parents.filter(parent => childrenOf(parent).length > 0)

  if (parents.length === 0) return config

  return { ...config, parents, childrenOf }
}

export function programPersonaSummary(
  grants: ProgramPersonaGrant[] | null = null,
): string[] {
  const list = grants ?? programPersonasForSession()
  return list.flatMap(grant => {
    const labels = grant.personas.map(persona => {
      if (persona === "student") return "Student"
      if (persona === "faculty") return "Faculty"
      return "Admin"
    })
    return [`${labels.join(" + ")} in ${grant.programId}`]
  })
}

export function mergeProgramPersonasIntoSession(
  grants: ProgramPersonaGrant[] | null,
  session: {
    role: WorkspaceRole
    opensAs: WorkspaceRole[]
    administeredProgramIds: string[] | null
    moduleAdminRoles: ModuleAdminRole[] | null
  },
): {
  administeredProgramIds: string[] | null
  moduleAdminRoles: ModuleAdminRole[] | null
  opensAs: WorkspaceRole[]
  role: WorkspaceRole
} {
  if (!grants || grants.length === 0) {
    return {
      administeredProgramIds: session.administeredProgramIds,
      moduleAdminRoles: session.moduleAdminRoles,
      opensAs: session.opensAs,
      role: session.role,
    }
  }

  const adminGrants = grants.filter(g => g.personas.includes("admin"))
  const administeredProgramIds =
    adminGrants.length > 0 ? adminGrants.map(g => g.programId) : null

  const moduleRoles = new Set<ModuleAdminRole>()
  for (const grant of adminGrants) {
    for (const role of grant.moduleAdminRoles ?? []) {
      moduleRoles.add(role)
    }
  }
  const moduleAdminRoles = moduleRoles.size > 0 ? [...moduleRoles] : null

  const allPersonas = new Set(grants.flatMap(g => g.personas))
  const opensAsRoles = DOOR_ORDER.filter(role => {
    if (role === "student") return allPersonas.has("student")
    if (role === "member") return allPersonas.has("faculty")
    if (role === "administrator") return allPersonas.has("admin")
    return false
  })
  const opensAs = opensAsRoles.length > 1 ? opensAsRoles : []

  let role = session.role
  if (!isWorkspaceRole(role)) role = "student"
  if (role === "administrator" && !allPersonas.has("admin")) {
    role = allPersonas.has("student") ? "student" : "member"
  }

  return { administeredProgramIds, moduleAdminRoles, opensAs, role }
}
